/**
 * PreviewAwareWeightSlot — weight column that switches between editable
 * input (active + excluded) and read-only preview diff cells.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import WeightInput, {
  type WeightInputVariant,
} from '@/features/flow-optimizer/tabs/standard/v2/paths/WeightInput';

import PreviewWeightCells from './PreviewWeightCells';
import { PreviewRowHoverCard } from './hover';
import { showExcludedBadge, type PreviewRow } from './preview-row.types';

export interface PreviewAwareWeightSlotProps {
  previewRow: PreviewRow | null;
  previewMode: boolean;
  value: number;
  onChange: (next: number) => void;
  ariaLabel: string;
  /** Row label for the hover/score popup (scoped to the metric cells). */
  name: string;
  active?: boolean;
  variant?: WeightInputVariant;
  integerMax?: number;
  forceReadOnly?: boolean;
  forceReadOnlyHint?: string;
}

const PreviewAwareWeightSlot: FC<PreviewAwareWeightSlotProps> = ({
  previewRow,
  previewMode,
  value,
  onChange,
  ariaLabel,
  name,
  active = true,
  variant = 'integer',
  integerMax,
  forceReadOnly = false,
  forceReadOnlyHint,
}) => {
  const excludedActive = showExcludedBadge(previewRow, active);

  if (previewMode && previewRow && !excludedActive) {
    // Hover/score popup is scoped to ONLY these current → new + Δ cells.
    return (
      <PreviewRowHoverCard
        name={name}
        row={previewRow}
        className="col-span-4 grid grid-cols-[3rem_0.75rem_3rem_3.25rem] items-center gap-x-1.5"
      >
        <PreviewWeightCells row={previewRow} />
      </PreviewRowHoverCard>
    );
  }

  return (
    <WeightInput
      variant={variant}
      integerMax={integerMax}
      value={value}
      onChange={onChange}
      ariaLabel={ariaLabel}
      readOnly={forceReadOnly}
      readOnlyHint={forceReadOnly ? forceReadOnlyHint : undefined}
    />
  );
};

export default PreviewAwareWeightSlot;
